import { useTranslation } from 'react-i18next';
import { HStack, Box, Text } from '@chakra-ui/react';
import type { StudentStatus, GuardianStatus } from '@/types/reunification';
import {
  STUDENT_STATUS_LABELS,
  STUDENT_STATUS_COLORS,
  GUARDIAN_STATUS_LABELS,
  GUARDIAN_STATUS_COLORS,
} from '@/types/reunification';
import type { RosterEnrollmentStatus } from '@/services/reunification';

interface StudentStatusBadgeProps {
  status: StudentStatus;
}

export function StudentStatusBadge({ status }: StudentStatusBadgeProps) {
  const { t } = useTranslation();
  return (
    <HStack gap={2}>
      <Box w={2} h={2} borderRadius="full" bg={STUDENT_STATUS_COLORS[status]} aria-hidden="true" />
      <Text fontSize="sm" fontWeight="medium" color="text.primary">
        {t(`status.${status}`, STUDENT_STATUS_LABELS[status])}
      </Text>
    </HStack>
  );
}

/**
 * Roster enrollment status treatment for the guardian students
 * payload (`GuardianStudentSerializer`). These are SIS enrollment states
 * (`active`/`inactive`/`transferred`/`withdrawn`), NOT the per-event
 * reunification `StudentStatus` flow — do not feed one into the other.
 */
const ROSTER_ENROLLMENT_STATUS_LABELS: Record<RosterEnrollmentStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
  transferred: 'Transferred',
  withdrawn: 'Withdrawn',
};

const ROSTER_ENROLLMENT_STATUS_COLORS: Record<RosterEnrollmentStatus, string> = {
  active: 'green.500',
  inactive: 'neutral.500',
  transferred: 'blue.500',
  withdrawn: 'orange.500',
};

interface RosterStatusBadgeProps {
  status: RosterEnrollmentStatus;
}

export function RosterStatusBadge({ status }: RosterStatusBadgeProps) {
  const { t } = useTranslation();
  return (
    <HStack gap={2}>
      <Box
        w={2}
        h={2}
        borderRadius="full"
        bg={ROSTER_ENROLLMENT_STATUS_COLORS[status]}
        aria-hidden="true"
      />
      <Text fontSize="sm" fontWeight="medium" color="text.primary">
        {t(`rosterStatus.${status}`, ROSTER_ENROLLMENT_STATUS_LABELS[status])}
      </Text>
    </HStack>
  );
}

interface GuardianStatusBadgeProps {
  status: GuardianStatus;
}

export function GuardianStatusBadge({ status }: GuardianStatusBadgeProps) {
  const { t } = useTranslation();
  return (
    <HStack gap={2}>
      <Box w={2} h={2} borderRadius="full" bg={GUARDIAN_STATUS_COLORS[status]} aria-hidden="true" />
      <Text fontSize="sm" fontWeight="medium" color="text.primary">
        {t(`status.${status}`, GUARDIAN_STATUS_LABELS[status])}
      </Text>
    </HStack>
  );
}
